import {ColumnProps} from 'antd/lib/table';
import React, {useMemo, useState} from 'react';
import {Avatar, Button, Card, Divider, Table, Space, Select} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import {FormattedNumber} from 'react-intl';
import {useModal} from '../../components/hooks/useModal';
import {useOrder} from '../hooks/useOrder';
import {UploadCertificateModal} from './UploadCertificateModal';

import { Order } from '../../hooks/data/models';
import { useArtworks } from '../../hooks/data/useArtworks';

interface ItemListCardProps {
  order: Order | undefined;
  isEditMode: boolean;
  loading: boolean;
  onDataEdited?: (updates: Partial<Order>) => void;
}

export const ItemListCard: React.FC<ItemListCardProps> = ({
  order, 
  loading = false,
  isEditMode = false,
  onDataEdited
}) => {
  const {visible, openModal, closeModal} = useModal();
  const { downloadCertificate } = useOrder();

  const [newArtworkId, setNewArtworkId] = useState<string>();
  const {
      artworks: availableArtworks, 
      isLoading: availableArtworksLoading,
      lastUpdated: availableArtworksLastUpdate, 
      refreshMainList: refreshAvailableArtworksMainList} = useArtworks();

  const handleItemRemoval = (idToRemove: string) => {
    if(order && onDataEdited){
      const index = order.lineItems.findIndex(el => {
        return el._id == idToRemove; 
      });
      if(index >= 0){
        let clonedArray = Object.assign([], order.lineItems);
        clonedArray.splice(index, 1)

        onDataEdited({lineItems: clonedArray});
      }
    }
  }

  const handleItemAddition = (idToAdd: string) => {
    setNewArtworkId(idToAdd);
    
    if(!onDataEdited || !order)
      return;

    const itemToAdd = availableArtworks.find(e => e._id === idToAdd);
    if(itemToAdd){
      let clonedLineItems = Object.assign([], order.lineItems);
      clonedLineItems.push({
        _id: itemToAdd._id,
        quantity: 1,
        price: itemToAdd.price ? itemToAdd.price.value : 0
      });

      const updates: any = {lineItems: clonedLineItems};

      if(order.products){
        let clonedProducts = Object.assign([], order.products);
        clonedProducts.push({
          _id: itemToAdd._id,
          name: itemToAdd.name,
          status: "",
          price: itemToAdd.price
        });
        updates["products"] = clonedProducts;
      }

      onDataEdited(updates);
    }
  }

  const handleDownload = async () => {
    if(!order)
      return;

    try {
      await downloadCertificate(order._id);
    } catch (e) {
      // TODO handle sentry + indicator
    }
  };

  const renderButtons = () => {
    if(!order)
      return;

    if(order.hasCertificates) {
      return <Button onClick={handleDownload} key="download-certificate-button">Download certificates</Button>;
    } else {
      return <Button onClick={openModal} key="upload-certificate-button">Upload certificates</Button>;
    }
  };

  const tableDataSource = useMemo(() => {
    if (!order?.lineItems || !order?.products) return [];

    return order.lineItems.map((item) => {
      
      const productDetails = order.products?.find((p) => p._id === item._id);

      return {
        ...item,
        product: productDetails,
      };
    })
  }, [order]);

  const columns: ColumnProps<any>[] = [{
    key: 'avatar',
    dataIndex: 'product',
    render: ({miniature, _id}) => (
      <Space>
        {isEditMode && <Button size={"small"} onClick={() => {handleItemRemoval(_id)}} danger><DeleteOutlined /></Button>}
        <Avatar src={miniature}/>
      </Space>
    )
  }, {
    title: 'Name',
    key: 'name',
    dataIndex: 'product',
    render: (product) => (
      <span>{product.name}</span>
    )
  }, {
    title: 'Quantity',
    key: 'quantity',
    dataIndex: 'quantity'
  }, {
    title: 'Price',
    key: 'price',
    dataIndex: 'product',
    render: (product) => (
      <FormattedNumber value={product.price.value} currency={product.price.currency} style={'currency'}/>
    )
  }, {
    title: 'Total',
    key: 'total',
    dataIndex: 'product',
    render: (product, record) => (
      <FormattedNumber
        value={product.price.value * record.quantity}
        currency={product.price.currency}
        style={'currency'}
      />
    )
  }];

  return (
    <Card
      title="Order Items"
      loading={loading}
      extra={[
        renderButtons(),
      ]}
    >
      {order && <Table
        dataSource={tableDataSource}
        columns={columns}
        pagination={false}
        rowKey={record => record._id} //row key
      />}

      {(order && isEditMode) && (
        <div>
          <Divider orientation={"left"}> Add item </Divider>
            <Select
              showSearch
              optionFilterProp="children"
              value={newArtworkId}
              onChange={handleItemAddition}
              style={{ width: "100%" }}
            >
              {availableArtworks.map(aw => (<Select.Option key={aw._id} value={aw._id}>{aw.name + ", " + aw.year}</Select.Option>))}
            </Select>          
        </div>
      )}

      {order && visible && (
        <UploadCertificateModal
          orderId={order._id}
          onClose={closeModal}
          onSuccess={closeModal}
        />
      )}
    </Card>
  )
};
